import { useEffect, useState } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { tokens } from '../../theme';
import { brandApi } from '../../lib/api';
import { Pegasus } from '../DreamlinkMark';

const SIGNAL = tokens.color.accent.sage; // maroon #590b11 — Dreamlink accent
const INK = tokens.color.ink[900];

interface Me {
  authenticated: true;
  role: string;
  email?: string;
  brand_id?: string;
  review_status?: string;
}

/**
 * Dark system bar — the Dreamlink brand signature. Full-bleed ink strip, mono
 * type, a pulsing dot indicating a live session.
 */
function SystemBar() {
  return (
    <div
      style={{
        gridColumn: '1 / -1',
        background: INK,
        color: 'rgba(255,255,255,0.55)',
        fontFamily: tokens.font.mono,
        fontSize: 11,
        letterSpacing: '0.04em',
        padding: '8px 24px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 24,
        flexWrap: 'wrap',
      }}
    >
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
        <Pegasus size={14} color="#F5F2EE" />
        <b style={{ color: '#fff', fontWeight: 500, letterSpacing: '0.14em' }}>DREAMLINK</b>
        <span style={{ opacity: 0.5 }}>STUDIO</span>
      </span>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
        <span
          style={{
            width: 6,
            height: 6,
            borderRadius: '50%',
            background: SIGNAL,
            boxShadow: `0 0 8px ${tokens.color.accent.sage}`,
            animation: 'livepulse 2s ease-in-out infinite',
          }}
        />
        BRAND PORTAL
      </span>
    </div>
  );
}

export function AppShell({ children }: { children: React.ReactNode }) {
  const [me, setMe] = useState<Me | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    document.body.classList.add('studio-blue');
    return () => document.body.classList.remove('studio-blue');
  }, []);

  useEffect(() => {
    brandApi.me().then((m) => {
      if (!m || m.role !== 'brand') {
        navigate('/login');
        return;
      }
      setMe(m as Me);
      setLoading(false);
    });
  }, [navigate]);

  if (loading) {
    return (
      <div style={{ padding: 48, color: tokens.color.ink[500] }}>Loading...</div>
    );
  }

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '240px 1fr',
        gridTemplateRows: 'auto 1fr',
        minHeight: '100vh',
        background: tokens.color.paper[50],
      }}
    >
      <SystemBar />
      <aside
        style={{
          background: tokens.color.paper['00'],
          borderRight: `1px solid ${tokens.color.line[100]}`,
          padding: 24,
        }}
      >
        <Link
          to="/"
          style={{ display: 'flex', alignItems: 'center', gap: 11, marginBottom: 32 }}
        >
          <Pegasus size={24} color={INK} />
          <span>
            <div
              style={{
                fontFamily: tokens.font.display,
                fontWeight: 500,
                letterSpacing: '0.06em',
                fontSize: 20,
                lineHeight: 1.1,
                color: tokens.color.ink[900],
              }}
            >
              Dreamlink Studio
            </div>
            <div
              style={{
                fontFamily: tokens.font.mono,
                fontSize: 10,
                letterSpacing: '0.18em',
                color: tokens.color.ink[500],
                textTransform: 'uppercase',
                marginTop: 3,
              }}
            >
              Brand portal
            </div>
          </span>
        </Link>
        <nav style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <ShellLink to="/app" label="Campaigns" end />
          <ShellLink to="/app/brief" label="New campaign" />
          <ShellLink to="/app/payment" label="Payment" />
        </nav>
      </aside>
      <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <header
          style={{
            height: 64,
            padding: '0 32px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            background: tokens.color.paper['00'],
            borderBottom: `1px solid ${tokens.color.line[100]}`,
          }}
        >
          {me?.email && (
            <span
              style={{
                fontFamily: tokens.font.mono,
                fontSize: 12,
                letterSpacing: '0.02em',
                color: tokens.color.ink[700],
                background: tokens.color.paper[100],
                border: `1px solid ${tokens.color.line[100]}`,
                borderRadius: 4,
                padding: '5px 10px',
              }}
            >
              {me.email}
            </span>
          )}
        </header>
        <main style={{ padding: 48, flex: 1 }}>{children}</main>
      </div>
    </div>
  );
}

function ShellLink({ to, label, end }: { to: string; label: string; end?: boolean }) {
  return (
    <NavLink
      to={to}
      end={end}
      style={({ isActive }) => ({
        display: 'block',
        padding: '10px 14px',
        borderRadius: 8,
        fontSize: 14,
        color: isActive ? tokens.color.ink[900] : tokens.color.ink[700],
        background: isActive ? tokens.color.paper[100] : 'transparent',
        borderLeft: isActive ? `3px solid ${tokens.color.ink[500]}` : '3px solid transparent',
        paddingLeft: 11,
      })}
    >
      {label}
    </NavLink>
  );
}
